
import React, { useEffect, useState } from "react";
import { getSla } from "../../../services/analytics/analyticsService";
import { SlaResponse } from "../../../interfaces/analytics";
import AnalyticsGraph from "./AnalyticsGraph";
import AnalyticsFilters from "./AnalyticsFilters";
import Pagination from "../utility/Pagination";

interface SlaAnalyticsProps {
  authToken: string;
}

const SlaAnalytics: React.FC<SlaAnalyticsProps> = ({ authToken }) => {
  const [data, setData] = useState<SlaResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    teamId: "",
    taskSubTypeId: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiParams = {
          ...filters,
          taskSubTypeId: filters.taskSubTypeId
            ? [parseInt(filters.taskSubTypeId, 10)]
            : undefined,
        };
        const response = await getSla(authToken, apiParams);
        setData(response);
        setCurrentPage(1); // Reset to first page on new data
      } catch (err) {
        setError("Failed to fetch SLA data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, filters]);

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data ? data.rows.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = data ? Math.ceil(data.rows.length / itemsPerPage) : 0;

  if (loading) {
    return <div className="text-center p-4">Loading SLA data...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="w-full">
      <h2 className="text-xl font-bold mb-4">SLA Analytics</h2>
      <AnalyticsFilters filters={filters} onFiltersChange={handleFiltersChange} />
      {data && data.rows.length > 0 ? (
        <>
          <AnalyticsGraph
            data={data.rows}
            xAxisKey="day"
            lineDataKey="onTimePct"
            lineName="On Time %"
          />
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Day</th>
                  <th className="py-2 px-4 border-b">Task SubType</th>
                  <th className="py-2 px-4 border-b">On Time %</th>
                  <th className="py-2 px-4 border-b">Median Cycle (hours)</th>
                  <th className="py-2 px-4 border-b">Overdue Count</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 border-b">{new Date(row.day).toLocaleDateString()}</td>
                    <td className="py-2 px-4 border-b">{row.taskSubTypeId}</td>
                    <td className="py-2 px-4 border-b">{row.onTimePct.toFixed(1)}%</td>
                    <td className="py-2 px-4 border-b">{row.medianCycleHours.toFixed(1)}</td>
                    <td className="py-2 px-4 border-b">{row.overdueCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="text-center p-4">No SLA data available for the selected filters.</div>
      )}
    </div>
  );
};

export default SlaAnalytics;
